import { jsx } from "panel";
import pick from "lodash/pick";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { ButtonEl } from "common/components/button";
import { DisplaySize, Placement } from "common/components/tooltip/types";
import { Directions } from "common/components/drop-menu/types";
import { SvgIconEl } from "common/components/svg-icon";
import { CountUsersOnceState } from "common/report/constants";
import { canUseCountUsersOnce } from "common/widgets/multi-attribution-menu/utils";
import { InsightsCountType } from "common/query-builder/query-entry-section/types";
import { PropertiesMenu } from "../properties-menu";
import { propertyItemToSegmentation } from "../properties-menu/util";
import { IncludeCustomPropertyType, isProperty } from "../properties-menu/types";
import { segmentationToPropertySelection } from "../segment-menu/util";
import { QueryBlockEl } from "../index";
import { PAGE_DURATION } from "../properties-menu/constants";
import {
  COUNT_USERS_ONCE_OPTIONS,
  DefaultCountUsersOnceType,
  SubSelectionTypes
} from "./constants";
import {
  generateCountUsersOnceMenuOptions,
  getAggregationIcon,
  getAggregationsSectionForCountType,
  getNestedPropertyIcon,
  getPropertyIcon,
  getSelectedAggregationLabel,
  getSubSelectionDescription,
  getSubSelectionsForCountType
} from "./util";
const CountUsersOnceSelection = ({
  comp,
  isOpen,
  countTypeSelection,
  selectionType,
  selectionIndex,
  onMenuChange
}) => {
  var _a;
  const isCountUsersOnceEnabled = canUseCountUsersOnce(
    (_a = comp.getContext(`currentProject`).get()) == null ? void 0 : _a.value,
    comp.attr(`metric-type`)
  );
  if (!isCountUsersOnceEnabled) {
    return null;
  }
  const countUsersOnceType = comp.attr(`selected`).countUsersOnceType || DefaultCountUsersOnceType.get(comp.attr(`metric-type`));
  const countUsersOnceState = comp.attr(`count-users-once-state`);
  const sections = generateCountUsersOnceMenuOptions({
    selectedCountUsersOnceType: countUsersOnceType,
    timePeriodLabel: comp.selectedTimePeriodLabel(),
    countUsersOnceState,
    metricType: comp.attr(`metric-type`)
  });
  const selectTooltipText = countUsersOnceState === CountUsersOnceState.Disabled ? `Breakdown required` : COUNT_USERS_ONCE_OPTIONS[countUsersOnceType].description({
    timePeriod: comp.selectedTimePeriodLabel()
  });
  return /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      open: isOpen,
      elref: `countUsersOnceSelect`,
      label: COUNT_USERS_ONCE_OPTIONS[countUsersOnceType].selectLabel(),
      "disable-search": true,
      disabled: countUsersOnceState === CountUsersOnceState.Disabled,
      "track-name": comp.attr(`track-name`),
      "menu-width": null,
      "menu-height": null,
      icon: { leftIcon: getAggregationIcon(countTypeSelection, selectionType), rightIcon: `double-caret` },
      "always-show-caret": true,
      theme: SelectTheme.Secondary,
      "disable-tooltip": comp.disableTooltips(),
      tooltip: !comp.disableTooltips() && {
        text: selectTooltipText,
        size: DisplaySize.Small,
        placement: Placement.Right
      },
      "menu-alignment": Directions.CenterRight
    },
    on: {
      menuChange: onMenuChange,
      change: ({ detail: { item: aggregation } }) => {
        comp.handleSelectAggregation(aggregation, selectionIndex);
      }
    },
    props: { sections }
  });
};
const AggregationSelection = ({
  comp,
  isOpen,
  countTypeSelection,
  selectionType,
  selectionIndex,
  operator,
  sections,
  onMenuChange
}) => {
  const { selectedAggregationOperator, selectedAggregationOperatorPerUser } = comp.state;
  return /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      open: isOpen,
      elref: `aggregationSelect`,
      label: getSelectedAggregationLabel({ metricType: comp.attr(`metric-type`), operator, countTypeSelection }),
      "disable-search": true,
      "track-name": comp.attr(`track-name`),
      "menu-width": 180,
      "menu-height": null,
      icon: { leftIcon: getAggregationIcon(countTypeSelection, selectionType), rightIcon: `double-caret` },
      "always-show-caret": true,
      theme: SelectTheme.Secondary,
      ...!comp.disableTooltips() && {
        tooltip: {
          text: getSubSelectionDescription({
            searchFirstLevelAggregation: selectionType === SubSelectionTypes.Aggregation,
            countTypeSelection,
            aggregationOperator: selectedAggregationOperator,
            aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
            dataGroupId: comp.attr(`data-group-id`),
            dataGroups: comp.attr(`data-groups`)
          }),
          size: DisplaySize.Small,
          placement: Placement.Right
        }
      },
      "menu-alignment": Directions.CenterRight
    },
    on: {
      menuChange: onMenuChange,
      change: ({ detail: { item: aggregation } }) => {
        comp.handleSelectAggregation(aggregation, selectionIndex);
      }
    },
    props: { sections }
  });
};
const PropertySelection = ({
  comp,
  selectionIndex,
  countTypeSelection
}) => {
  const { isPropertyMenuOpen, selectedProperty } = comp.state;
  let otherAggregationOptions = comp.attr(`other-aggregation-options`);
  if (countTypeSelection.label === InsightsCountType.PropertyValuePerUser) {
    otherAggregationOptions = otherAggregationOptions.filter((opt) => opt.value !== PAGE_DURATION.value);
  }
  const attributes = {
    ...pick(comp.attrs(), Object.keys(PropertiesMenu.attrsSchema)),
    selected: selectedProperty,
    type: `properties`,
    open: isPropertyMenuOpen,
    "selector-icon": getPropertyIcon(selectedProperty),
    "selector-right-icon": `double-caret`,
    "selector-type": `button`,
    "show-special-date-property-unit": true,
    theme: SelectTheme.Secondary,
    "track-name": comp.attr(`track-name`),
    "menu-alignment": Directions.CenterRight,
    "menu-offset": 4,
    "other-aggregation-options": otherAggregationOptions
  };
  return /* @__PURE__ */ jsx(QueryBlockEl, {
    attrs: attributes,
    props: {
      contextPanelParentElement: comp
    },
    on: {
      select: (ev) => {
        comp.handleSelectProperty(ev, selectionIndex);
        comp.update({
          isPropertyMenuOpen: false
        });
      }
    }
  });
};
const NestedPropertySelection = ({ comp, selectionIndex }) => {
  var _a;
  const { isNestedPropertyMenuOpen, selectedProperty } = comp.state;
  if (!isProperty(selectedProperty))
    return null;
  const attributes = {
    ...pick(comp.attrs(), Object.keys(PropertiesMenu.attrsSchema)),
    "all-data-groups": false,
    selected: segmentationToPropertySelection(selectedProperty.listItemGroup),
    "include-custom-properties": IncludeCustomPropertyType.None,
    "event-names": null,
    type: `properties`,
    open: isNestedPropertyMenuOpen,
    "selector-icon": getNestedPropertyIcon(selectedProperty == null ? void 0 : selectedProperty.listItemGroup),
    "selector-right-icon": `double-caret`,
    "show-special-date-property-unit": true,
    "selector-type": `button`,
    theme: SelectTheme.Secondary,
    "track-name": comp.attr(`track-name`),
    "menu-alignment": Directions.CenterRight,
    "menu-offset": 4
  };
  return /* @__PURE__ */ jsx(QueryBlockEl, {
    attrs: attributes,
    params: {
      listProperty: propertyItemToSegmentation(
        comp.state.selectedProperty,
        (_a = comp.state.selectedProperty) == null ? void 0 : _a.resourceType
      )
    },
    props: {
      contextPanelParentElement: comp
    },
    on: {
      select: (ev) => {
        comp.handleSelectProperty(ev, selectionIndex, {
          isNestedProperty: true
        });
        comp.update({
          isNestedPropertyMenuOpen: false
        });
      }
    }
  });
};
const SubSelections = ({
  comp,
  selections,
  countTypeIndex
}) => {
  const { countTypeSelections, selectedAggregationOperator, selectedAggregationOperatorPerUser } = comp.state;
  const countTypeSelection = comp.state.countTypeSelections[countTypeIndex];
  return /* @__PURE__ */ jsx(null, null, selections.map((selection, idx) => {
    const previousSelectionType = selections[idx - 1];
    const canExpandCurrentSubSelection = comp.canExpandCurrentSubSelection(countTypeIndex, previousSelectionType);
    if (!canExpandCurrentSubSelection)
      return null;
    switch (selection) {
      case SubSelectionTypes.Property:
        return /* @__PURE__ */ jsx(PropertySelection, {
          comp,
          selectionIndex: idx,
          countTypeSelection
        });
      case SubSelectionTypes.NestedProperty:
        return /* @__PURE__ */ jsx(NestedPropertySelection, {
          comp,
          selectionIndex: idx
        });
      case SubSelectionTypes.AggregationPerUser:
        return /* @__PURE__ */ jsx(AggregationSelection, {
          comp,
          countTypeSelection,
          selectionType: SubSelectionTypes.AggregationPerUser,
          selectionIndex: idx,
          operator: selectedAggregationOperatorPerUser,
          sections: getAggregationsSectionForCountType({
            countTypeSelections,
            countTypeIndex,
            property: comp.state.selectedProperty,
            aggregationOperator: selectedAggregationOperator,
            subSelectionType: SubSelectionTypes.AggregationPerUser,
            dataGroupId: comp.attr(`data-group-id`),
            dataGroups: comp.attr(`data-groups`)
          }),
          isOpen: comp.state.isAggregationPerUserMenuOpen,
          onMenuChange: (ev) => comp.update({ isAggregationPerUserMenuOpen: ev.detail.isOpen })
        });
      case SubSelectionTypes.Aggregation:
        return /* @__PURE__ */ jsx(AggregationSelection, {
          comp,
          countTypeSelection,
          selectionType: SubSelectionTypes.Aggregation,
          selectionIndex: idx,
          operator: selectedAggregationOperator,
          sections: getAggregationsSectionForCountType({
            countTypeSelections,
            countTypeIndex,
            property: comp.state.selectedProperty,
            subSelectionType: SubSelectionTypes.Aggregation,
            metricType: comp.attr(`metric-type`),
            dataGroupId: comp.attr(`data-group-id`),
            dataGroups: comp.attr(`data-groups`)
          }),
          isOpen: comp.state.isAggregationMenuOpen,
          onMenuChange: (ev) => comp.update({ isAggregationMenuOpen: ev.detail.isOpen })
        });
      case SubSelectionTypes.CountUsersOnce:
        return /* @__PURE__ */ jsx(CountUsersOnceSelection, {
          comp,
          countTypeSelection,
          selectionType: SubSelectionTypes.CountUsersOnce,
          selectionIndex: idx,
          isOpen: comp.state.isCountUsersOnceMenuOpen,
          onMenuChange: (ev) => comp.update({ isCountUsersOnceMenuOpen: ev.detail.isOpen })
        });
      default:
        return null;
    }
  }));
};
export function template() {
  const { countTypeSelections, selectedCountTypeIndex } = this.state;
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-count-menu"
  }, this.attr(`open`) && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".count-header"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".count-title"
  }, "Measuring"), this.attr(`advanced-menu-sections`).length > 0 && /* @__PURE__ */ jsx("div", {
    sel: ".count-advance-options"
  }, /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      open: this.state.isAdvancedMenuOpen,
      "always-show-caret": true,
      label: this.attr(`advanced-menu-label`) || `Advanced`,
      theme: SelectTheme.Tertiary,
      size: ButtonSize.Small,
      "menu-alignment": Directions.Right,
      "menu-width": 184,
      "disable-first-item-padding": true,
      elref: `advancedCountMenuSelect`
    },
    props: {
      sections: this.attr(`advanced-menu-sections`)
    },
    on: {
      change: this.handleAdvancedMenuChange
    }
  }))), /* @__PURE__ */ jsx("div", {
    sel: ".count-option-list"
  }, countTypeSelections.map((countTypeSelection, index) => {
    var _a;
    const subSelections = getSubSelectionsForCountType({
      countTypeSelection,
      property: this.state.selectedProperty
    });
    const isSelected = selectedCountTypeIndex === index;
    return /* @__PURE__ */ jsx("div", {
      sel: ".count-option-wrapper"
    }, /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".count-option",
      class: {
        truncated: true
      },
      attrs: {
        theme: isSelected ? ButtonTheme.Primary : ButtonTheme.QueryBuilder,
        "not-focusable": true,
        tooltip: countTypeSelection.tooltip && !this.disableTooltips() ? {
          placement: Placement.Right,
          ...(_a = countTypeSelection.tooltip) != null ? _a : {}
        } : null
      },
      on: {
        click: () => this.handleSelectCountType(countTypeSelection, index)
      }
    }, /* @__PURE__ */ jsx("div", {
      sel: ".count-option-label-wrapper"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".option-label",
      attrs: {
        elref: `countOptionLabel`
      }
    }, countTypeSelection.label), countTypeSelection.value.expanderIcon ? /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".option-icon-button",
      attrs: {
        icon: countTypeSelection.value.expanderIcon,
        size: ButtonSize.Small,
        theme: isSelected ? ButtonTheme.Primary : ButtonTheme.Tertiary
      },
      on: {
        click: (ev) => this.handleSelectIcon(ev, countTypeSelection, index)
      }
    }) : subSelections.length > 0 ? /* @__PURE__ */ jsx(SvgIconEl, {
      sel: ".option-icon",
      attrs: { icon: isSelected ? `caret-up` : `caret-down` }
    }) : null)), /* @__PURE__ */ jsx(SubSelections, {
      comp: this,
      selections: subSelections,
      countTypeIndex: index
    }));
  }))));
}
